import { message } from 'antd';
import axios, { AxiosError } from 'axios';

// API错误响应接口
export interface ApiErrorResponse {
  statusCode: number;
  message: string | string[];
  timestamp: string;
  path: string;
}

// 错误处理类
export class ErrorHandler {
  /**
   * 类型守卫：须用 axios 官方判断（超时等场景下 `response` 可能为 undefined，仅用 `in` 会误判）
   */
  private static isAxiosError(error: unknown): error is AxiosError<ApiErrorResponse> {
    return axios.isAxiosError(error);
  }
  /**
   * 处理API错误
   * @param error Axios错误对象
   * @param defaultMessage 默认错误消息
   * @param showMessage 是否显示消息提示
   */
  static handleApiError(
    error: unknown,
    defaultMessage: string = '操作失败',
    showMessage: boolean = true
  ): void {
    // 类型守卫：检查是否为AxiosError
    if (this.isAxiosError(error)) {
      const { response } = error;
      
      if (response) {
        const { status, data } = response;
      let errorMessage = defaultMessage;
      
      // 从响应数据中获取错误消息
      if (data?.message) {
        if (Array.isArray(data.message)) {
          errorMessage = data.message.join(', ');
        } else {
          errorMessage = data.message;
        }
      }
      
      // 根据状态码显示不同的错误消息
      switch (status) {
        case 400:
          errorMessage = errorMessage || '请求参数错误';
          break;
        case 401:
          errorMessage = '登录已过期，请重新登录';
          // 401错误会自动重定向到登录页，不需要显示消息
          showMessage = false;
          break;
        case 403:
          errorMessage = errorMessage || '权限不足';
          break;
        case 404:
          errorMessage = errorMessage || '请求的资源不存在';
          break;
        case 409:
          errorMessage = errorMessage || '数据冲突';
          break;
        case 422:
          errorMessage = errorMessage || '数据验证失败';
          break;
        case 500:
          errorMessage = '服务器内部错误，请稍后重试';
          break;
        default:
          errorMessage = errorMessage || `请求失败 (${status})`;
      }
      
      if (showMessage) {
        message.error(errorMessage);
      }
      
        // 记录详细错误信息到控制台
        console.error(`API Error ${status}:`, {
          message: errorMessage,
          path: data?.path,
          timestamp: data?.timestamp,
          originalError: error
        });
      } else if (error.request) {
        const isTimeout = error.code === 'ECONNABORTED';
        const networkErrorMessage = isTimeout
          ? '请求超时，请确认后端已启动且地址（REACT_APP_API_URL）正确'
          : '网络连接失败，请检查网络设置';
        if (showMessage) {
          message.error(networkErrorMessage);
        }
        console.error(isTimeout ? 'Request timeout:' : 'Network Error:', error.message);
      } else {
        // 其他错误
        const configErrorMessage = '请求配置错误';
        if (showMessage) {
          message.error(configErrorMessage);
        }
        console.error('Request Config Error:', error.message);
      }
    } else {
      // 处理非AxiosError的情况
      const unknownErrorMessage = '未知错误';
      if (showMessage) {
        message.error(unknownErrorMessage);
      }
      console.error('Unknown Error:', error);
    }
  }
  
  /**
   * 获取友好的错误消息
   * @param error 错误对象
   * @returns 友好的错误消息
   */
  static getErrorMessage(error: unknown): string {
    if (this.isAxiosError(error)) {
      const { response } = error;
      
      if (response?.data?.message) {
        const message = response.data.message;
        return Array.isArray(message) ? message.join(', ') : message;
      }
      
      if (response) {
        switch (response.status) {
          case 400:
            return '请求参数错误';
          case 401:
            return '登录已过期';
          case 403:
            return '权限不足';
          case 404:
            return '资源不存在';
          case 409:
            return '数据冲突';
          case 422:
            return '数据验证失败';
          case 500:
            return '服务器内部错误';
          default:
            return `请求失败 (${response.status})`;
        }
      }
      
      if (error.request) {
        return '网络连接失败';
      }
      
      return '未知错误';
    }
    
    return '未知错误';
  }
  
  /**
   * 检查是否为特定状态码错误
   * @param error 错误对象
   * @param statusCode 状态码
   * @returns 是否为指定状态码
   */
  static isStatusCode(error: unknown, statusCode: number): boolean {
    return this.isAxiosError(error) && error.response?.status === statusCode;
  }
  
  /**
   * 检查是否为网络错误
   * @param error 错误对象
   * @returns 是否为网络错误
   */
  static isNetworkError(error: unknown): boolean {
    return this.isAxiosError(error) && !error.response && error.request;
  }
}

// 导出便捷函数（必须 bind，否则作为独立函数调用时 static 方法内 this 丢失）
export const handleApiError = ErrorHandler.handleApiError.bind(ErrorHandler);
export const getErrorMessage = ErrorHandler.getErrorMessage.bind(ErrorHandler);
export const isStatusCode = ErrorHandler.isStatusCode.bind(ErrorHandler);
export const isNetworkError = ErrorHandler.isNetworkError.bind(ErrorHandler);
